/**
 * ShareTargetPage — the Web Share Target landing surface (`${BASE_URL}share-target?text=…`); `App` routes here via `isShareTargetPath`.
 * Shared text is shown for review and sent via `broadcastClip` ONLY behind an explicit confirm tap (never auto-sent), then returns to the inbox.
 * SECURITY: the shared text is clip content — never logged; a send failure logs a tag only.
 */

import { Box, Button, Spinner, Text } from "@reflecto/client-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { webStorage } from "../adapters/storage.web";
import { useStorage } from "../hooks/useStorage";
import { messages } from "../i18n";
import { logWarn } from "../lib/log";
import { readSharedText } from "../lib/router";
import { broadcastClip } from "../services/clipboard";
import styles from "./ShareTargetPage.module.css";

/** Leave the share-target URL and return to the SPA inbox at BASE_URL. */
function returnToApp(): void {
  // Replace (not push) so the back button doesn't re-trigger the share.
  window.location.replace(import.meta.env.BASE_URL);
}

type SendState = "idle" | "sending" | "done" | "error";

export function ShareTargetPage() {
  const { value: paired, hydrated } = useStorage("paired");
  // Read the shared payload once on mount — the URL doesn't change.
  const shared = useMemo(() => readSharedText(), []);
  const [state, setState] = useState<SendState>("idle");
  const [message, setMessage] = useState<string>("");
  // The post-success "return to inbox" timer. Stored so a cancel tap or
  // an unmount within the 900ms window cancels it — otherwise a stale
  // timer would `location.replace(BASE_URL)` and kick the user out.
  const returnTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearReturnTimer = useCallback(() => {
    if (returnTimer.current !== undefined) {
      clearTimeout(returnTimer.current);
      returnTimer.current = undefined;
    }
  }, []);

  // Cancel any pending return timer on unmount / navigation away.
  useEffect(() => clearReturnTimer, [clearReturnTimer]);

  const handleCancel = useCallback(() => {
    clearReturnTimer();
    returnToApp();
  }, [clearReturnTimer]);

  const handleSend = useCallback(async () => {
    if (!shared.trim()) {
      setState("error");
      setMessage(messages.clipboard.sendEmpty);
      return;
    }
    setState("sending");
    try {
      const { token, secretKey, selfDeviceName } = await webStorage.get(
        "token",
        "secretKey",
        "selfDeviceName"
      );
      const result = await broadcastClip(token, secretKey, shared, {
        originLabel: selfDeviceName,
      });
      if (result.total === 0) {
        setState("error");
        setMessage(messages.clipboard.sendNoDevices);
      } else if (result.failed.length === 0) {
        setState("done");
        setMessage(messages.clipboard.sendSuccess(result.sent));
        // Brief pause so the success line is visible, then return.
        returnTimer.current = setTimeout(returnToApp, 900);
      } else {
        setState("error");
        setMessage(messages.clipboard.sendPartial(result.sent, result.total));
      }
    } catch (err) {
      logWarn("messages", "clip_share_target_failed", err);
      setState("error");
      setMessage(messages.clipboard.sendFailed);
    }
  }, [shared]);

  if (!hydrated) {
    return (
      <Box align="center" justify="center" paddingY="xl">
        <Spinner size="lg" tone="brand" label={messages.common.loading} />
      </Box>
    );
  }

  return (
    <Box direction="column" gap="md" padding="lg" align="stretch">
      <Text as="h1" size="l" weight="semibold" tone="primary">
        {messages.clipboard.shareTitle}
      </Text>

      {!paired ? (
        <>
          <Text size="s" tone="secondary">
            {messages.clipboard.shareNotPaired}
          </Text>
          <Button variant="primary" onClick={returnToApp}>
            {messages.clipboard.shareOpenApp}
          </Button>
        </>
      ) : (
        <>
          <Text size="xs" tone="tertiary">
            {messages.clipboard.shareReview}
          </Text>
          <Box as="pre" padding="md" className={styles.preview}>
            {shared || messages.clipboard.sendEmpty}
          </Box>

          {message && (
            <Text size="s" tone={state === "done" ? "success" : "negative"} role="status">
              {message}
            </Text>
          )}

          <Box direction="row" gap="sm" justify="end">
            <Button variant="ghost" onClick={handleCancel} disabled={state === "sending"}>
              {messages.clipboard.shareCancel}
            </Button>
            <Button
              variant="primary"
              loading={state === "sending"}
              disabled={state === "sending" || state === "done" || !shared.trim()}
              onClick={() => {
                void handleSend();
              }}
            >
              {messages.clipboard.shareSend}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}
